// client/src/pages/PTO/WhosOutPanel.tsx
// Weekly "Who's Out" panel — office-grouped cards (consecutive same-type days
// collapsed into a single date-range row) + holiday/birthday/anniversary banners.

import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  CalendarCheck,
  AlertTriangle,
} from 'lucide-react';
import type { PTOEntry, PTOEmployee, PTOHoliday, EntryType } from './ptoTypes';
import {
  weekDatesFor,
  weekDatesForFull,
  mondayOf,
  formatDate,
  formatTime12,
  dayAbbr,
  isWeekend,
  deadlineDatesForYear,
  getEntryTypeStyle,
} from './ptoUtils';
import { OFFICES } from './ptoConstants';

// ─── Props ──────────────────────────────────────────────────────────────────

interface WhosOutPanelProps {
  entries: PTOEntry[];
  employees: PTOEmployee[];
  holidays: PTOHoliday[];
  currentWeek: Date; // Monday of the current week
  onWeekChange: (dir: -1 | 1) => void;
  onJumpTo: (monday: Date) => void;
  onEmployeeClick: (emp: PTOEmployee) => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatWeekRange(monday: Date): string {
  const fri = new Date(monday);
  fri.setDate(monday.getDate() + 4);
  const mOpts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const start = monday.toLocaleDateString('en-US', mOpts);
  const end = fri.toLocaleDateString('en-US', {
    ...mOpts,
    year: 'numeric',
  });
  return `${start} – ${end}`;
}

function isThisWeek(monday: Date): boolean {
  const today = mondayOf(new Date());
  return (
    monday.getFullYear() === today.getFullYear() &&
    monday.getMonth() === today.getMonth() &&
    monday.getDate() === today.getDate()
  );
}

/** Check if a month/day falls within the given week dates. */
function matchesBirthdayInWeek(
  birthday: string | null,
  weekDates: string[],
): string | null {
  if (!birthday) return null;
  const bMonth = birthday.slice(5, 7);
  const bDay = birthday.slice(8, 10);
  return weekDates.find((d) => d.slice(5, 7) === bMonth && d.slice(8, 10) === bDay) ?? null;
}

/** Format an entry's out-of-office window. Returns '' when no times are set. */
function formatEntryTimeRange(seg: {
  startTime: string | null;
  endTime: string | null;
}): string {
  const s = formatTime12(seg.startTime);
  const e = formatTime12(seg.endTime);
  if (s && e) return `${s} – ${e}`;
  if (e && !s) return `out at ${e}`;
  if (s && !e) return `from ${s}`;
  return '';
}

/** "2026-06-29" → "6/29" (the year already shows in the week header). */
function mmdd(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${Number(m)}/${Number(d)}`;
}

/** A run of consecutive weekdays sharing one entry type. */
interface DaySegment {
  startDate: string;
  endDate: string;
  entryType: EntryType;
  startTime: string | null;
  endTime: string | null;
  note: string | null;
}

/** Collapse a person's week entries (sorted by date) into runs of consecutive
 *  weekdays of the same type. Only full-day entries collapse, so partial-day
 *  appointments keep their own row (and their time). */
function collapseSegments(empEntries: PTOEntry[], weekDates: string[]): DaySegment[] {
  const out: DaySegment[] = [];
  for (const e of empEntries) {
    const last = out[out.length - 1];
    const fullDay = !e.startTime && !e.endTime;
    if (
      last &&
      fullDay &&
      !last.startTime &&
      !last.endTime &&
      last.entryType === e.entryType &&
      weekDates.indexOf(last.endDate) === weekDates.indexOf(e.entryDate) - 1
    ) {
      last.endDate = e.entryDate; // extend the run
    } else {
      out.push({
        startDate: e.entryDate,
        endDate: e.entryDate,
        entryType: e.entryType,
        startTime: e.startTime,
        endTime: e.endTime,
        note: e.note,
      });
    }
  }
  return out;
}

/** Check if hire anniversary falls within the given week dates. Returns years if match. */
function matchesAnniversaryInWeek(
  hireDate: string | null,
  weekDates: string[],
): { date: string; years: number } | null {
  if (!hireDate) return null;
  const hMonth = hireDate.slice(5, 7);
  const hDay = hireDate.slice(8, 10);
  const hYear = Number(hireDate.slice(0, 4));
  const match = weekDates.find(
    (d) => d.slice(5, 7) === hMonth && d.slice(8, 10) === hDay,
  );
  if (!match) return null;
  const matchYear = Number(match.slice(0, 4));
  const years = matchYear - hYear;
  if (years <= 0) return null;
  return { date: match, years };
}

// ─── Component ──────────────────────────────────────────────────────────────

const WhosOutPanel: React.FC<WhosOutPanelProps> = ({
  entries,
  employees,
  holidays,
  currentWeek,
  onWeekChange,
  onJumpTo,
  onEmployeeClick,
}) => {
  const weekDates = useMemo(() => weekDatesFor(currentWeek), [currentWeek]);
  // Full Mon-Sun span used for banner matching (so weekend birthdays/holidays still show).
  const weekDatesAll = useMemo(() => weekDatesForFull(currentWeek), [currentWeek]);
  const thisWeek = isThisWeek(currentWeek);

  // Hidden date input for the week-picker popover.
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const openPicker = () => {
    const input = dateInputRef.current;
    if (!input) return;
    // showPicker() is available on modern browsers; fall back to focus + click.
    if (typeof (input as any).showPicker === 'function') {
      (input as any).showPicker();
    } else {
      input.focus();
      input.click();
    }
  };
  const handlePickDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value; // YYYY-MM-DD
    if (!val) return;
    const [y, m, d] = val.split('-').map(Number);
    onJumpTo(mondayOf(new Date(y, m - 1, d)));
  };

  // Holidays falling in this week (Mon-Sun)
  const weekHolidays = useMemo(
    () => holidays.filter((h) => weekDatesAll.includes(h.holidayDate)),
    [holidays, weekDatesAll],
  );

  // Deadlines in this week (covers both years in case the week spans Dec-Jan)
  const weekDeadlines = useMemo(() => {
    const years = new Set(weekDatesAll.map((d) => Number(d.slice(0, 4))));
    const all = Array.from(years).flatMap((y) => deadlineDatesForYear(y));
    return all.filter((d) => weekDatesAll.includes(d.date));
  }, [weekDatesAll]);

  // Birthdays in this week (Mon-Sun)
  const weekBirthdays = useMemo(() => {
    const results: { emp: PTOEmployee; date: string }[] = [];
    for (const emp of employees) {
      const d = matchesBirthdayInWeek(emp.birthday, weekDatesAll);
      if (d) results.push({ emp, date: d });
    }
    return results;
  }, [employees, weekDatesAll]);

  // Anniversaries in this week (Mon-Sun)
  const weekAnniversaries = useMemo(() => {
    const results: { emp: PTOEmployee; date: string; years: number }[] = [];
    for (const emp of employees) {
      const match = matchesAnniversaryInWeek(emp.hireDate, weekDatesAll);
      if (match) results.push({ emp, date: match.date, years: match.years });
    }
    return results;
  }, [employees, weekDatesAll]);

  // Entries grouped by employee (Mon-Fri).
  // "Friday Work" is excluded — it shows in the Flex Fridays panel instead.
  const grouped = useMemo(() => {
    const weekEntries = entries.filter(
      (e) => weekDates.includes(e.entryDate) && e.entryType !== 'Friday Work',
    );
    const map = new Map<string, PTOEntry[]>();
    for (const entry of weekEntries) {
      const list = map.get(entry.staffIndex) ?? [];
      list.push(entry);
      map.set(entry.staffIndex, list);
    }
    // Sort entries within each group by date
    for (const list of map.values()) {
      list.sort((a, b) => a.entryDate.localeCompare(b.entryDate));
    }
    return map;
  }, [entries, weekDates]);

  const empMap = useMemo(() => {
    const m = new Map<string, PTOEmployee>();
    for (const e of employees) m.set(e.staffIndex, e);
    return m;
  }, [employees]);

  // Sorted employee keys
  const sortedStaff = useMemo(() => {
    return Array.from(grouped.keys()).sort((a, b) => {
      const ea = empMap.get(a);
      const eb = empMap.get(b);
      if (!ea || !eb) return 0;
      return ea.sortOrder - eb.sortOrder || ea.displayName.localeCompare(eb.displayName);
    });
  }, [grouped, empMap]);

  // Group the sorted staff by office (OFFICES order) for the office sections.
  const officeGroups = useMemo(() => {
    type Person = { emp: PTOEmployee; entries: PTOEntry[] };
    const groups: { office: string; color: string; people: Person[] }[] = [];
    const seen = new Set<string>();
    const peopleFor = (predicate: (emp: PTOEmployee) => boolean): Person[] =>
      sortedStaff
        .map((si) => ({ emp: empMap.get(si), entries: grouped.get(si) ?? [] }))
        .filter((p): p is Person => !!p.emp && predicate(p.emp));
    for (const o of OFFICES) {
      const people = peopleFor((emp) => emp.office === o.value);
      people.forEach((p) => seen.add(p.emp.staffIndex));
      if (people.length > 0) groups.push({ office: o.value, color: o.color, people });
    }
    // Defensive bucket for any office value not in the OFFICES list (e.g. 'Unknown').
    const rest = peopleFor((emp) => !seen.has(emp.staffIndex));
    if (rest.length > 0) groups.push({ office: 'Other', color: '#8090a0', people: rest });
    return groups;
  }, [sortedStaff, empMap, grouped]);

  const hasBanners =
    weekHolidays.length > 0 ||
    weekBirthdays.length > 0 ||
    weekAnniversaries.length > 0 ||
    weekDeadlines.length > 0;
  const hasCards = sortedStaff.length > 0;
  const isEmpty = !hasBanners && !hasCards;

  return (
    <Wrap className="card">
      {/* Header */}
      <Header className="hd">
        {!thisWeek && (
          <TodayBtnRow>
            <TodayBtn onClick={() => onJumpTo(mondayOf(new Date()))}>
              <CalendarCheck size={12} />
              Jump to This Week
            </TodayBtn>
          </TodayBtnRow>
        )}
        <NavRow>
          <NavBtn onClick={() => onWeekChange(-1)} aria-label="Previous week">
            <ChevronLeft size={16} />
          </NavBtn>
          <WeekLabel
            onClick={openPicker}
            role="button"
            tabIndex={0}
            aria-label="Jump to a different week"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openPicker();
              }
            }}
          >
            {thisWeek && <ThisWeekTag>This Week</ThisWeekTag>}
            <WeekLabelText>
              {formatWeekRange(currentWeek)}
              <Calendar size={11} style={{ marginLeft: 6, opacity: 0.55 }} />
            </WeekLabelText>
            <HiddenDateInput
              ref={dateInputRef}
              type="date"
              onChange={handlePickDate}
              tabIndex={-1}
              aria-hidden="true"
            />
          </WeekLabel>
          <NavBtn onClick={() => onWeekChange(1)} aria-label="Next week">
            <ChevronRight size={16} />
          </NavBtn>
        </NavRow>
      </Header>

      <Body className="bd">
        {/* Banners */}
        {weekDeadlines.map((d) => (
          <DeadlineBanner key={d.date}>
            <BannerEmoji>
              <AlertTriangle size={18} />
            </BannerEmoji>
            <BannerText>
              <strong>{d.name}</strong>
              <BannerDate>{formatDate(d.date)}</BannerDate>
            </BannerText>
          </DeadlineBanner>
        ))}

        {weekHolidays.map((h) => (
          <HolidayBanner key={h.id}>
            <BannerEmoji>{h.emoji ?? '🏖️'}</BannerEmoji>
            <BannerText>
              <strong>{h.name}</strong>
              <BannerDate>{formatDate(h.holidayDate)}</BannerDate>
            </BannerText>
          </HolidayBanner>
        ))}

        {weekBirthdays.map(({ emp, date }) => (
          <BirthdayBanner key={emp.staffIndex}>
            <BannerEmoji>🎂</BannerEmoji>
            <BannerText>
              <strong>{emp.displayName}</strong> — Birthday
              <BannerDate>
                {formatDate(date)}
                {isWeekend(date) && (
                  <WeekendTag>{dayAbbr(date)}</WeekendTag>
                )}
              </BannerDate>
            </BannerText>
          </BirthdayBanner>
        ))}

        {weekAnniversaries.map(({ emp, date, years }) => (
          <AnniversaryBanner key={emp.staffIndex}>
            <BannerEmoji>🏆</BannerEmoji>
            <BannerText>
              <strong>{emp.displayName}</strong> — {years} Year{years !== 1 ? 's' : ''}
              <BannerDate>{formatDate(date)}</BannerDate>
            </BannerText>
          </AnniversaryBanner>
        ))}

        {/* Who's out — grouped by office, consecutive same-type days collapsed */}
        {hasCards && (
          <OfficeSections>
            {officeGroups.map(({ office, color, people }) => (
              <OfficeSection key={office}>
                <OfficeHeader>
                  <OfficePill $color={color}>{office}</OfficePill>
                  <OfficeCount>{people.length}</OfficeCount>
                </OfficeHeader>
                <CardGrid>
                  {people.map(({ emp, entries: empEntries }) => {
                    const hasTentative = empEntries.some(
                      (e) => e.entryType === 'Tentative PTO',
                    );
                    const segments = collapseSegments(empEntries, weekDates);
                    return (
                      <EmpCard
                        key={emp.staffIndex}
                        $borderColor={color}
                        $tentative={hasTentative}
                      >
                        <EmpName onClick={() => onEmployeeClick(emp)}>
                          {emp.displayName}
                        </EmpName>

                        {hasTentative && <PendingBadge>PENDING</PendingBadge>}

                        <EntryList>
                          {segments.map((seg, i) => {
                            const style = getEntryTypeStyle(seg.entryType);
                            const isRange = seg.startDate !== seg.endDate;
                            const timeRange = isRange ? '' : formatEntryTimeRange(seg);
                            return (
                              <EntryRow key={`${seg.startDate}-${seg.entryType}-${i}`}>
                                <SegDate>
                                  {isRange
                                    ? `${dayAbbr(seg.startDate)} ${mmdd(seg.startDate)} – ${dayAbbr(seg.endDate)} ${mmdd(seg.endDate)}`
                                    : `${dayAbbr(seg.startDate)} ${mmdd(seg.startDate)}`}
                                </SegDate>
                                <TypeBadge $bg={style.bg} $color={style.color}>
                                  {seg.entryType}
                                </TypeBadge>
                                {timeRange && <EntryTime>({timeRange})</EntryTime>}
                                {!isRange && seg.note && <EntryNote>{seg.note}</EntryNote>}
                              </EntryRow>
                            );
                          })}
                        </EntryList>
                      </EmpCard>
                    );
                  })}
                </CardGrid>
              </OfficeSection>
            ))}
          </OfficeSections>
        )}

        {/* Empty state */}
        {isEmpty && <EmptyMsg>No absences this week!</EmptyMsg>}
      </Body>
    </Wrap>
  );
};

export default WhosOutPanel;

// ─── Styled Components ──────────────────────────────────────────────────────

const Wrap = styled.div``;

const Header = styled.div``;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const TodayBtnRow = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
`;

const TodayBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  border: 1px solid rgba(122, 162, 255, 0.35);
  border-radius: 999px;
  background: rgba(122, 162, 255, 0.08);
  color: var(--accent);
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(122, 162, 255, 0.18);
  }
`;

const HiddenDateInput = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  pointer-events: none;
`;

const NavBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    color: var(--text);
  }
`;

const WeekLabel = styled.span`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
  gap: 2px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 6px;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

const WeekLabelText = styled.span`
  display: inline-flex;
  align-items: center;
`;

const ThisWeekTag = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--accent);
`;

/* ── Banners ─────────────────────────────────────────────── */

const BannerBase = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  border-left: 3px solid;
`;

const HolidayBanner = styled(BannerBase)`
  background: rgba(87, 211, 140, 0.08);
  border-left-color: var(--good);
`;

const BirthdayBanner = styled(BannerBase)`
  background: rgba(255, 107, 107, 0.08);
  border-left-color: #ff6b6b;
`;

const AnniversaryBanner = styled(BannerBase)`
  background: rgba(100, 210, 255, 0.08);
  border-left-color: var(--accent2);
`;

const DeadlineBanner = styled(BannerBase)`
  background: rgba(255, 107, 107, 0.1);
  border-left-color: #ff6b6b;
  color: #ff9a9a;
`;

const WeekendTag = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 1px 5px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  background: rgba(255, 158, 100, 0.18);
  color: #ff9e64;
  border-radius: 4px;
`;

const BannerEmoji = styled.span`
  font-size: 20px;
  flex-shrink: 0;
`;

const BannerText = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--text);
  flex-wrap: wrap;

  strong {
    font-weight: 600;
  }
`;

const BannerDate = styled.span`
  font-size: 11px;
  color: var(--muted);
`;

/* ── Office sections + employee cards ────────────────────── */

const OfficeSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OfficeSection = styled.div``;

const OfficeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const OfficePill = styled.span<{ $color: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  white-space: nowrap;
  background: ${({ $color }) => $color}26;
  color: ${({ $color }) => $color};
`;

const OfficeCount = styled.span`
  font-size: 11px;
  color: var(--muted);
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
`;

const EmpCard = styled.div<{ $borderColor: string; $tentative: boolean }>`
  background: rgba(255, 255, 255, 0.03);
  border: ${({ $tentative }) =>
    $tentative ? '1px dashed var(--warn)' : '1px solid var(--border)'};
  border-left: 3px solid ${({ $borderColor }) => $borderColor};
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const EmpName = styled.button`
  all: unset;
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    color: var(--accent);
  }
`;

const PendingBadge = styled.span`
  display: inline-block;
  align-self: flex-start;
  padding: 1px 7px;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  background: rgba(255, 209, 102, 0.15);
  color: var(--warn);
`;

const EntryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const EntryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 12px;
`;

const SegDate = styled.span`
  color: var(--muted);
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
`;

const TypeBadge = styled.span<{ $bg: string; $color: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

const EntryTime = styled.span`
  font-size: 11px;
  color: var(--muted);
  font-weight: 500;
`;

const EntryNote = styled.span`
  font-size: 11px;
  color: var(--muted);
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 120px;
`;

const EmptyMsg = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: var(--muted);
  font-size: 13px;
`;
